// frontend/src/composables/useCalendarEmail.js
import { DateTime } from 'luxon';
import { sendScheduleEmail as apiSendScheduleEmail } from '@/lib/api';
import { DISPLAY_TIMEZONE } from '@/lib/timeUtils';

/**
 * Composable for calendar email functionality
 */
export function useCalendarEmail() {
  /**
   * Generate default subject line based on store and calendar view
   * @param {Object} store - Store object with name
   * @param {Object} calendarApi - FullCalendar API instance
   * @returns {string} - Generated subject line
   */
  const generateDefaultSubject = (store, calendarApi) => {
    if (!store || !calendarApi) return 'Schedule';

    const viewType = calendarApi.view.type;
    const isWeekView = viewType === 'timeGridWeek';
    const currentDate = calendarApi.getDate();

    if (isWeekView) {
      // For week view: "Stoneham Schedule: Nov 30 – Dec 6, 2025"
      const weekStart = DateTime.fromJSDate(calendarApi.view.activeStart, { zone: DISPLAY_TIMEZONE });
      const weekEnd = DateTime.fromJSDate(calendarApi.view.activeEnd, { zone: DISPLAY_TIMEZONE }).minus({ days: 1 }); // activeEnd is exclusive

      // Format start and end dates
      const startFormatted = weekStart.toFormat('MMM d');
      const endFormatted = weekEnd.toFormat('MMM d, yyyy');

      return `${store.name} Schedule: ${startFormatted} – ${endFormatted}`;
    } else {
      // For month view: "Schedule for Stoneham - December 2025"
      const dt = DateTime.fromJSDate(currentDate, { zone: DISPLAY_TIMEZONE });
      const monthYear = dt.toFormat('MMMM yyyy');

      return `Schedule for ${store.name} - ${monthYear}`;
    }
  };

  /**
   * Send schedule email via API
   * @param {Object} params - Email parameters
   * @param {number} params.store_id - Store ID
   * @param {number} params.year - Year
   * @param {number} params.month - Month
   * @param {string} params.start_date - Start date for weekly view (ISO format)
   * @param {Array<string>} params.recipients - Array of email addresses
   * @param {string} params.subject - Email subject
   * @param {string} params.message_html - HTML message body
   * @returns {Promise} - API response
   */
  const sendScheduleEmail = async (params) => {
    try {
      const response = await apiSendScheduleEmail(params);
      return response.data;
    } catch (error) {
      console.error('Error sending schedule email:', error);
      throw error;
    }
  };

  /**
   * High-level function to email a calendar schedule
   * @param {Object} options - Email options
   * @param {Object} options.calendarApi - FullCalendar API instance
   * @param {Object} options.selectedStore - Currently selected store
   * @param {Object} options.emailData - Email data from modal (recipients, subject, messageHtml)
   * @param {string} options.viewType - Calendar view type
   * @returns {Promise} - API response
   */
  const emailCalendar = async ({ calendarApi, selectedStore, emailData, viewType }) => {
    const isWeekView = viewType === 'timeGridWeek';
    const currentDate = DateTime.fromJSDate(calendarApi.getDate(), { zone: DISPLAY_TIMEZONE });

    const params = {
      store_id: selectedStore.id,
      recipients: emailData.recipients,
      subject: emailData.subject,
      message_html: emailData.messageHtml,
    };

    if (isWeekView) {
      // For weekly view, send start_date
      const weekStart = DateTime.fromJSDate(calendarApi.view.activeStart, { zone: DISPLAY_TIMEZONE });
      params.start_date = weekStart.toISODate();
    } else {
      // For monthly view, send year and month
      params.year = currentDate.year;
      params.month = currentDate.month;
    }

    return sendScheduleEmail(params);
  };

  return {
    generateDefaultSubject,
    sendScheduleEmail,
    emailCalendar,
  };
}
